import React, { CSSProperties } from "react";
import * as Sentry from "@sentry/react";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import { cn } from "@src/lib/utils";
import { SxProps, Theme } from "@mui/material/styles";
import { PromptInput } from "@src/components/ai-elements/prompt-input/PromptInput";
import { PromptInputBody } from "@src/components/ai-elements/prompt-input/PromptInputBody";
import type { PromptInputError } from "@src/components/ai-elements/prompt-input/PromptInputError";
import { PromptInputFooter } from "@src/components/ai-elements/prompt-input/PromptInputFooter";
import { PromptInputSubmit } from "@src/components/ai-elements/prompt-input/PromptInputSubmit";
import { PromptInputTools } from "@src/components/ai-elements/prompt-input/PromptInputTools";
import { InputGroupAddon } from "@src/components/ui/input-group";
import { checkChatAttachment } from "@intelligems/ig-utils";
import { IgTypography, mergeSx } from "@intelligems/ig-theme";

import { TextInputHandler } from "./TextInputHandler";
import Stack from "@mui/material/Stack";

export interface PromptComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Blocks Submit and Enter without blocking draft editing. */
  submitDisabled?: boolean;
  /** Shows the stop-button in place of submit and spins the submit icon. */
  streaming?: boolean;
  onStop?: () => void;
  /** Renders in place of the composer entirely, same as `UserInput`'s error state. */
  error?: string | null;
  /** MIME allow-list for the hidden file input, e.g. `listAllowedChatAttachmentMimes().join(",")`. Omit when the caller doesn't support attachments. */
  accept?: string;
  maxFiles?: number;
  maxFileSize?: number;
  /** Attachment chip row — e.g. `<AttachmentPreview />` — rendered above the textarea. */
  promptInputPreview?: React.ReactNode;
  /** Left-aligned footer content — e.g. an attach button. */
  promptInputTools?: React.ReactNode;
  /** Panel-supplied offer, rendered above `suggestions`. */
  composerAsk?: React.ReactNode;
  /** Rendered above the composer, e.g. suggestion chips. */
  suggestions?: React.ReactNode;
  /**
   * Full override for the trailing submit control — e.g. a branded CTA
   * button instead of the default icon-only submit/stop button. Wire it to
   * the same `onSubmit`/`disabled` state directly; it renders as a sibling
   * of `promptInputTools` inside `PromptInputFooter`, not inside the form's
   * native submit path, so it must call `onSubmit` itself.
   */
  submit?: React.ReactNode;
  /**
   * `"default"` stacks the footer (tools + submit) below the textarea,
   * growing to a full multi-line composer — matches every execution-panel
   * chat. `"inline"` keeps tools + submit in the SAME row as the textarea,
   * for a compact single-line composer (e.g. the AskAtlas home-dashboard
   * prompt box).
   */
  variant?: "default" | "inline";
  divWrapperSx?: CSSProperties;
  /** Configures the parts the composer draws itself. See `PromptComposerSlotProps`. */
  slotProps?: PromptComposerSlotProps;
}

/**
 * Props for the parts the composer renders itself, keyed by the part.
 *
 * Each is merged caller-last over the component's own, so omitting one leaves
 * every existing chat exactly as it was. A part a caller replaces outright
 * takes its own props with it and is not configured from here.
 */
export interface PromptComposerSlotProps {
  /** The composer's surface — the filled, rounded box the input sits on. */
  surface?: { sx?: SxProps<Theme> };
  /** The default submit control. Ignored when `submit` replaces it. */
  submit?: { className?: string };
}

/**
 * The composer UI shared by every execution-panel-based AI chat (`VivChat`,
 * `QaBotChat`, etc. via `UserInput`) — the AI-gradient glow, the
 * attachment-aware `PromptInput` shell, and the submit/stop button — with no
 * dependency on `ExecutionPanelContext`. `UserInput` wraps this and supplies
 * its props from the execution panel's context; any other AI surface (e.g.
 * `AskAtlasBuildExperience`, which manages its own local turn state instead
 * of a workflow-backed `aiChat`) can render this directly instead.
 */
export const PromptComposer = ({
  value,
  onChange,
  onSubmit,
  placeholder = "What can I help you with today?",
  disabled = false,
  submitDisabled = false,
  streaming = false,
  onStop,
  error,
  accept,
  maxFiles,
  maxFileSize,
  promptInputPreview,
  promptInputTools,
  composerAsk,
  suggestions,
  submit,
  variant = "default",
  divWrapperSx,
  slotProps,
}: PromptComposerProps) => {
  if (error) {
    return <IgTypography>{error}</IgTypography>;
  }

  const toolsNode = <PromptInputTools>{promptInputTools}</PromptInputTools>;
  const submissionDisabled = disabled || submitDisabled;
  const handlePromptInputError = (promptInputError: PromptInputError) => {
    if (promptInputError.code === "attachment_read") {
      if (promptInputError.cause) {
        Sentry.captureException(promptInputError.cause, {
          tags: { promptInputErrorCode: promptInputError.code },
        });
      }
      toast.error(
        "We couldn't read one or more attachments. Try sending your message again."
      );
      return;
    }

    toast.error(promptInputError.message);
  };
  // Wrapped in a div so this isn't a direct child of `InputGroupAddon` — its
  // `inline-end` variant applies a `has-[>button]:mr-[-0.45rem]` negative
  // margin tuned for shadcn's own icon-only ghost buttons, which cancels out
  // our padding and pins a custom `submit` button flush to the corner.
  const submitNode = (
    <div>
      {submit ?? (
        <PromptInputSubmit
          className={cn("h-8!", slotProps?.submit?.className)}
          disabled={submissionDisabled}
          status={streaming ? "streaming" : undefined}
          onStop={onStop}
        />
      )}
    </div>
  );

  return (
    <div
      className="grid shrink-0 gap-3"
      style={{ paddingTop: "16px", ...divWrapperSx }}
    >
      {composerAsk}
      {suggestions}
      <GlowWrapper slotProps={slotProps}>
        <PromptInput
          accept={accept}
          maxFiles={maxFiles}
          maxFileSize={maxFileSize}
          multiple
          onError={handlePromptInputError}
          onSubmit={onSubmit}
          validateFile={validateChatAttachmentFile}
          inputGroupStyle={
            variant === "inline" ? { padding: 8, height: "auto" } : undefined
          }
        >
          <PromptInputBody>
            {promptInputPreview}
            <TextInputHandler
              value={value}
              onChange={onChange}
              onSubmit={onSubmit}
              submitDisabled={submissionDisabled}
              placeholder={placeholder}
              disabled={disabled}
              className={variant === "inline" ? "min-h-9 py-1.5" : undefined}
            />
          </PromptInputBody>
          {variant === "inline" ? (
            // `InputGroupAddon`'s own `inline-end` variant adds its own
            // py-1.5/pr-3 on top of the InputGroup's 8px padding above —
            // zeroed out here so the buttons sit exactly 8px from the edge,
            // not 8px + the addon's own built-in inset.
            <InputGroupAddon align="inline-end" className="gap-1 py-0 pr-0">
              <Stack direction="row" spacing={1} alignItems="center">
                {toolsNode}
                {submitNode}
              </Stack>
            </InputGroupAddon>
          ) : (
            // Two direct children (not one wrapping node) — `PromptInputFooter`
            // relies on `justify-content: space-between` across its children to
            // pin tools left / submit right; a single wrapper collapses both to
            // one flex item and the space-between has nothing to distribute.
            <PromptInputFooter>
              {toolsNode}
              {submitNode}
            </PromptInputFooter>
          )}
        </PromptInput>
      </GlowWrapper>
    </div>
  );
};

const validateChatAttachmentFile = (
  file: File
): PromptInputError | undefined => {
  const result = checkChatAttachment({
    filename: file.name,
    mediaType: file.type,
    size: file.size,
  });
  if (result.accept) {
    return;
  }
  if (
    result.code === "too_large" &&
    file.type.startsWith("image/") &&
    file.type !== "image/svg+xml"
  ) {
    return;
  }

  return {
    code: result.code === "too_large" ? "max_file_size" : "accept",
    message: result.reason,
  };
};

/**
 * Wraps the composer with an ambient AI gradient glow that's revealed only
 * while the input is focused. The glow sits behind an opaque surface so it only
 * ever shows as a halo around the input and never bleeds across the interior
 * (the surface is always present, so it can't flash through during the
 * fade-out). The glow bleeds beyond this box — callers with `overflow: hidden`
 * ancestors (e.g. `ExecutionPanelChat` / `Conversation`) will clip it there.
 */
const GlowWrapper = ({
  children,
  slotProps,
}: {
  children: React.ReactNode;
  slotProps?: Pick<PromptComposerSlotProps, "surface">;
}) => (
  <Box
    className="w-full min-w-0 px-1"
    sx={{
      position: "relative",
      "&:focus-within .ig-prompt-input-glow": { opacity: 0.2 },
    }}
  >
    <Box
      aria-hidden
      className="ig-prompt-input-glow"
      sx={(theme) => ({
        position: "absolute",
        inset: theme.spacing(-1),
        borderRadius: theme.spacing(3),
        // eslint-disable-next-line ig-fe-requirements/no-hardcoded-hex-colors -- no close ig-theme match
        background: `linear-gradient(202deg, #ffa977 5%, ${theme.igPrimitives.pink[400]} 38%, ${theme.igPrimitives.purple[700]} 68%, ${theme.igPrimitives.teal[300]} 96%)`,
        filter: `blur(${theme.spacing(1)})`,
        mixBlendMode: "multiply",
        opacity: 0,
        transition: "opacity 0.3s ease",
        pointerEvents: "none",
        zIndex: 0,
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      })}
    />
    <Box
      sx={mergeSx(
        (theme) => ({
          position: "relative",
          zIndex: 1,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "calc(var(--radius) - 2px)",
        }),
        slotProps?.surface?.sx
      )}
    >
      {children}
    </Box>
  </Box>
);
